<template>
    <div class="control-section">
      <MenuComponent />
      <div class="workspace">
        <div class="editor-and-console-container">
            <EditorComponent @editor-ready="onEditorReady" />

            <!-- Para o console -->
            <div v-show="showConsole" class="resize-handle-console" @mousedown="startResizingConsole($event)"></div>
            <ConsoleComponent :showConsole="showConsole" :consoleMessages="consoleMessages" @clearConsole="clearConsole" />
        </div>
        <!-- Para o chat -->
        <div v-show="showChat" class="resize-handle-chat" @mousedown="startResizingChat($event)"></div>
        <ChatComponent :showChat="showChat" :csmlContent="csmlContent" :sessionData="sessionData" :metaData="metaData"/>
      </div>
      <div class="footer">
        <button @click="toggleConsole">>_Terminal</button>
        <button @click="toggleChat" class="right-button">Visualização</button>
      </div>
      <input ref="fileInput" type="file" accept=".csml,.txt,text/plain" style="display: none;" @change="onFileChosen" />
      <ModalComponent ref="modal" />
    </div>
  </template>

  <script>
  import * as monaco from 'monaco-editor';
  import CsmlEngineApi from '@/services/CsmlEngineApi';
  import { EventBus } from './event-bus.js';
  import { loadSessionData, loadMetadata, saveMetadata } from './session.js';
  import { escapeHtml, describeError } from './utils.js';
  import pkg from '../package.json';

  import MenuComponent from './components/MenuComponent.vue'
  import EditorComponent from './components/EditorComponent.vue'
  import ConsoleComponent from './components/ConsoleComponent.vue'
  import ChatComponent from './components/ChatComponent.vue'
  import ModalComponent from './components/ModalComponent.vue'

  const SHORTCUTS = [
    ['Ctrl+S', 'Salvar o bot (.csml)'],
    ['Ctrl+Z / Ctrl+Y', 'Desfazer / refazer'],
    ['Ctrl+F / Ctrl+H', 'Localizar / substituir'],
    ['Ctrl+/', 'Comentar linha'],
    ['Ctrl+Espaço', 'Sugestões de código'],
    ['Alt+↑ / Alt+↓', 'Mover linha'],
    ['Ctrl+D', 'Selecionar próxima ocorrência'],
    ['F1', 'Paleta de comandos do editor'],
    ['Enter (no chat)', 'Enviar mensagem'],
  ];

  export default {
    components: {
      MenuComponent,
      EditorComponent,
      ConsoleComponent,
      ChatComponent,
      ModalComponent
    },
    data() {
      return {
        sessionData: loadSessionData(),
        metaData: loadMetadata(),
        envData: {},
        editor: null,
        fileName: 'bot.csml',
        showConsole: true,
        showChat: true,
        csmlContent: '',
        consoleMessages: [
            { message: "Bem vindo ao editor de fluxo.", timestamp: this.getCurrentTimestamp() },
            { message: "Clique em 'Compilar' para compilar seu BOT e rodar na janela de visualização na lateral direita.", timestamp: this.getCurrentTimestamp() }
        ]
      };
    },
    methods: {
        log(message) {
            this.consoleMessages.push({ message, timestamp: this.getCurrentTimestamp() });
        },
        clearConsole() {
            this.consoleMessages = [];
        },
        toggleConsole() {
            this.showConsole = !this.showConsole;
        },
        toggleChat() {
            this.showChat = !this.showChat;
        },
        getCurrentTimestamp() {
            const now = new Date();
            return now.toTimeString().substring(0, 8);
        },
        onEditorReady(editor) {
            this.editor = editor;
        },

        // ---- Menus ----
        onMenuAction(action) {
            const handlers = {
                'arquivo-novo': this.newFile,
                'arquivo-abrir': () => this.$refs.fileInput.click(),
                'arquivo-salvar': this.saveFile,
                'editar-desfazer': () => this.editorTrigger('undo'),
                'editar-refazer': () => this.editorTrigger('redo'),
                'editar-recortar': () => this.clipboard('cut'),
                'editar-copiar': () => this.clipboard('copy'),
                'editar-colar': this.paste,
                'editar-localizar': () => this.editorAction('actions.find'),
                'editar-substituir': () => this.editorAction('editor.action.startFindReplaceAction'),
                'editar-comentar': () => this.editorAction('editor.action.commentLine'),
                'ver-terminal': this.toggleConsole,
                'ver-visualizador': this.toggleChat,
                'ver-tela-cheia': this.toggleFullscreen,
                'ferramentas-metadata': this.editMetadata,
                'ferramentas-status': this.showServerStatus,
                'ferramentas-reiniciar': this.restartServer,
                'compilar': this.compile,
                'ajuda-docs': () => window.open('https://docs.csml.dev/', '_blank', 'noopener'),
                'ajuda-atalhos': this.showShortcuts,
                'ajuda-sobre': this.showAbout,
            };
            if (handlers[action]) {
                handlers[action]();
            } else if (action.startsWith('ferramentas-')) {
                // Ações da conversa ficam no ChatComponent
                EventBus.$emit('chat-action', action.replace('ferramentas-', ''));
            }
        },
        setEditorContent(content) {
            if (!this.editor) return;
            const model = this.editor.getModel();
            this.editor.pushUndoStop();
            this.editor.executeEdits('menu', [{ range: model.getFullModelRange(), text: content }]);
            this.editor.pushUndoStop();
            this.editor.setPosition({ lineNumber: 1, column: 1 });
            this.editor.focus();
        },
        async newFile() {
            const ok = await this.$refs.modal.open({
                title: 'Novo bot',
                html: 'Apagar o código atual do editor? Dá para desfazer com Ctrl+Z.',
                ok: 'Apagar', cancel: 'Cancelar'
            });
            if (ok) {
                this.fileName = 'bot.csml';
                this.setEditorContent('');
            }
        },
        onFileChosen(event) {
            const file = event.target.files[0];
            event.target.value = '';
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                this.fileName = file.name;
                this.setEditorContent(String(reader.result));
                this.log(`Arquivo aberto: ${escapeHtml(file.name)}`);
            };
            reader.readAsText(file);
        },
        saveFile() {
            const blob = new Blob([this.csmlContent], { type: 'text/plain;charset=utf-8' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = this.fileName;
            link.click();
            URL.revokeObjectURL(link.href);
            this.log(`Arquivo salvo: ${escapeHtml(this.fileName)}`);
        },
        editorTrigger(command) {
            if (!this.editor) return;
            this.editor.focus();
            this.editor.trigger('menu', command, null);
        },
        editorAction(id) {
            if (!this.editor) return;
            this.editor.focus();
            this.editor.getAction(id).run();
        },
        async clipboard(kind) {
            if (!this.editor) return;
            const selection = this.editor.getSelection();
            const text = this.editor.getModel().getValueInRange(selection);
            if (!text) return;
            try {
                await navigator.clipboard.writeText(text);
            } catch (e) {
                this.log('<span style="color: red;">O navegador bloqueou a área de transferência; use Ctrl+C / Ctrl+X.</span>');
                return;
            }
            if (kind === 'cut') {
                this.editor.executeEdits('menu', [{ range: selection, text: '' }]);
            }
            this.editor.focus();
        },
        async paste() {
            if (!this.editor) return;
            let text;
            try {
                text = await navigator.clipboard.readText();
            } catch (e) {
                this.log('<span style="color: red;">O navegador bloqueou a área de transferência; use Ctrl+V.</span>');
                return;
            }
            this.editor.executeEdits('menu', [{ range: this.editor.getSelection(), text, forceMoveMarkers: true }]);
            this.editor.focus();
        },
        toggleFullscreen() {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen().catch(() =>
                    this.log('<span style="color: red;">O navegador não permitiu tela cheia aqui.</span>'));
            }
        },
        async editMetadata() {
            const text = await this.$refs.modal.open({
                title: 'Metadata enviada ao bot',
                html: 'É o <code>_metadata</code> que o bot recebe em cada mensagem. O MA manda <code>conversation</code>, <code>contact</code> e <code>config</code>.',
                textarea: JSON.stringify(this.metaData, null, 2),
                ok: 'Salvar', cancel: 'Cancelar',
                validate: value => {
                    try {
                        const parsed = JSON.parse(value);
                        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? '' : 'A metadata precisa ser um objeto JSON.';
                    } catch (e) {
                        return `JSON inválido: ${e.message}`;
                    }
                }
            });
            if (text !== null) {
                this.metaData = JSON.parse(text);
                saveMetadata(this.metaData);
                this.log('<span style="color: green;">Metadata atualizada.</span>');
            }
        },
        async showServerStatus() {
            try {
                const { data } = await CsmlEngineApi.getStatus();
                this.log(`Engine do CSML: v${escapeHtml(data.engine_version)}, Database: ${escapeHtml(data.database_type)}, Servidor pronto: ${data.server_ready}`);
            } catch (error) {
                this.log(`<span style="color: red;">Servidor CSML sem resposta: ${describeError(error)}</span>`);
            }
            try {
                const { data } = await CsmlEngineApi.serverHealth();
                const minutes = Math.floor(data.uptime / 60);
                this.log(`Supervisor: no ar há ${minutes} min, ${data.restarts} reinício(s)` +
                    (data.last_reason ? ` (último: ${escapeHtml(data.last_reason)})` : ''));
            } catch (e) {
                this.log('Supervisor do csml-server não disponível neste ambiente.');
            }
        },
        async restartServer() {
            const ok = await this.$refs.modal.open({
                title: 'Reiniciar servidor CSML',
                html: 'Reinicia o csml-server de debug. Use quando ele parar de responder. Conversas e memórias ficam salvas no banco.',
                ok: 'Reiniciar', cancel: 'Cancelar'
            });
            if (!ok) return;
            try {
                await CsmlEngineApi.restartServer();
            } catch (error) {
                this.log(`<span style="color: red;">Não foi possível reiniciar: ${describeError(error)}</span>`);
                return;
            }
            this.log('Reiniciando o servidor CSML...');
            for (let i = 0; i < 20; i++) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                try {
                    await CsmlEngineApi.getStatus();
                    this.log('<span style="color: green;">Servidor CSML de volta.</span>');
                    return;
                } catch (e) { /* ainda subindo */ }
            }
            this.log('<span style="color: red;">O servidor CSML não voltou em 20s.</span>');
        },
        setErrorMarkers(errors) {
            if (!this.editor) return;
            monaco.editor.setModelMarkers(this.editor.getModel(), 'csml', errors.map(err => ({
                severity: monaco.MarkerSeverity.Error,
                message: err.message,
                startLineNumber: err.start_line || 1,
                startColumn: err.start_column || 1,
                endLineNumber: err.end_line || err.start_line || 1,
                endColumn: err.end_column || 1000
            })));
        },
        async compile() {
            let validation;
            try {
                validation = (await CsmlEngineApi.validateBot(this.sessionData, this.envData, this.csmlContent)).data;
            } catch (error) {
                this.log(`<span style="color: red;">Erro ao validar o bot: ${describeError(error)}</span>`);
                return;
            }
            if (!validation.valid) {
                this.setErrorMarkers(validation.errors);
                this.log(`<span style="color: red;">Falha na validação do bot, ${validation.errors.length} erro(s):</span>`);
                validation.errors.forEach(err => {
                    this.log(`<span style="color: red;">Linha ${err.start_line}: ${escapeHtml(err.message.trim())}</span>`);
                });
                if (validation.errors[0] && this.editor) {
                    this.editor.revealLineInCenter(validation.errors[0].start_line);
                }
                return;
            }
            this.setErrorMarkers([]);
            this.log('<span style="color: green;">Bot VALIDADO com sucesso!</span>');
            try {
                const { data } = await CsmlEngineApi.versionBot(this.sessionData, this.envData, this.csmlContent);
                this.log(`<span style="color: green;">Versão ID: ${escapeHtml(data.version_id)} (CSML Engine: ${escapeHtml(data.engine_version)})</span>`);
            } catch (error) {
                this.log(`<span style="color: red;">Erro ao gerar versão do bot: ${describeError(error)}</span>`);
            }
            // Código novo começa conversa nova: retomar um hold de outra versão
            // do código dá resultados estranhos
            EventBus.$emit('chat-action', 'nova-conversa');
        },
        showShortcuts() {
            const rows = SHORTCUTS.map(([keys, what]) => `<tr><td><kbd>${keys}</kbd></td><td>${what}</td></tr>`).join('');
            this.$refs.modal.open({ title: 'Atalhos do teclado', html: `<table>${rows}</table>` });
        },
        async showAbout() {
            let engine = 'indisponível';
            try {
                engine = (await CsmlEngineApi.getStatus()).data.engine_version;
            } catch (e) { /* servidor fora */ }
            this.$refs.modal.open({
                title: 'Editor CSML',
                html: `Editor e depurador de bots CSML do Multiatendedor.<br><br>` +
                    `Editor: v${pkg.version}<br>Engine CSML: v${escapeHtml(engine)}<br>` +
                    `Usuário de teste: ${escapeHtml(this.sessionData.userID)}<br><br>` +
                    `<a href="https://github.com/flipfloplab/csml_editor" target="_blank" rel="noopener">github.com/flipfloplab/csml_editor</a>`
            });
        },

        // ---- Redimensionamento ----
        startResizingChat(event) {
            event.preventDefault();
            document.addEventListener('mousemove', this.resizeChat);
            document.addEventListener('mouseup', this.stopResizingChat);
        },
        resizeChat(event) {
            let newWidth = window.innerWidth - event.clientX;
            newWidth = Math.max(400, newWidth);
            newWidth = Math.min(window.innerWidth - 400, newWidth);

            this.$el.querySelector('.chat-container').style.width = `${newWidth}px`;
            this.$el.querySelector('.chat-container').style.maxWidth = `${newWidth}px`;
        },
        stopResizingChat() {
            document.removeEventListener('mousemove', this.resizeChat);
            document.removeEventListener('mouseup', this.stopResizingChat);
        },
        startResizingConsole(event) {
            event.preventDefault();
            document.addEventListener('mousemove', this.resizeConsole);
            document.addEventListener('mouseup', this.stopResizingConsole);
        },
        resizeConsole(event) {
            let newHeight = window.innerHeight - event.clientY;
            newHeight = Math.max(200, newHeight);
            newHeight = Math.min(window.innerHeight - 200, newHeight);
            this.$el.querySelector('.console-container').style.height = `${newHeight}px`;
        },
        stopResizingConsole() {
            document.removeEventListener('mousemove', this.resizeConsole);
            document.removeEventListener('mouseup', this.stopResizingConsole);
        },
    },
    mounted() {
        this.showServerStatus();
    },
    created() {
        EventBus.$on('add-console-message', this.log);
        EventBus.$on('update:csmlContent', content => { this.csmlContent = content; });
        EventBus.$on('menu-action', this.onMenuAction);
    },
    beforeDestroy() {
        EventBus.$off('add-console-message');
        EventBus.$off('update:csmlContent');
        EventBus.$off('menu-action');
    }
  }
  </script>

<style>

    .control-section {
        display: flex;
        flex-direction: column;
        height: 100vh; /* Use vh para ocupar a tela inteira */
    }

    .workspace {
        flex: 1;
        display: flex;
        overflow: hidden; /* Previne o overflow do conteúdo principal */
    }

    .editor-and-console-container {
        flex: 3;
        display: flex;
        flex-direction: column; /* Editor acima do console */
        overflow: auto; /* Adiciona rolagem se necessário */
    }

    .footer {
        background-color: #292828;
        color: #9b9b9b;
        padding: 5px;
        text-align: left;
        display: flex;
        justify-content: space-between; /* Botões alinhados aos lados */
    }

    .clear-button, .footer button {
        padding: 2px 5px;
        margin-right: 10px;
        background-color: #3a3a3a;
        color: white;
        border: none;
        cursor: pointer;
        font-size: 1.1rem; /* Fonte menor */
    }

    .clear-button:hover, .footer button:hover {
        background-color: #7d7d7e; 
    }

    .resize-handle-chat {
        height: 100%;
        width: 1px;
        background-color: #c5c5c5;
        cursor: ew-resize; /* Cursor estilo leste-oeste para indicar redimensionamento */
    }

    .resize-handle-console {
        height: 1px;
        width: 100%;
        background-color: #c5c5c5;
        cursor: ns-resize; /* Cursor estilo norte-sul para indicar redimensionamento */
    }
</style>